import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, Menu, X, MessageCircle, ChevronRight, ChevronDown, Wrench, Search, CreditCard, HandCoins, Facebook, Instagram } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { companyInfo } from '../../data/mockData';
import { usePageTransition } from '../../contexts/PageTransitionContext';
import { trackEvent } from '../../utils/analytics';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const servicesRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { navigateWithTransition } = usePageTransition();

  const handleNav = (e, path) => {
    e.preventDefault();
    if (location.pathname === path) return;
    setMobileOpen(false);
    setServicesOpen(false);
    navigateWithTransition(path);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const serviceLinks = [
    { path: '/servicos/oficina', label: 'Serviços de Oficina', icon: Wrench },
    { path: '/servicos/consultadoria', label: 'Encomendar uma Viatura', icon: Search },
    { path: '/servicos/credito', label: 'Intermediação de Crédito', icon: CreditCard },
    { path: '/servicos/compramos', label: 'Compramos o seu Carro', icon: HandCoins },
  ];

  const navLinks = [
    { path: '/', label: 'Início' },
    { path: '/viaturas', label: 'Viaturas' },
    { path: '/servicos', label: 'Serviços', hasDropdown: true },
    { path: '/sobre', label: 'Sobre Nós' },
    { path: '/contacto', label: 'Contacto' },
  ];

  const isActive = (path) => path === '/servicos' ? location.pathname.startsWith('/servicos') : location.pathname === path;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-[#0a0a0a]/95 backdrop-blur-xl shadow-2xl shadow-black/20 py-3'
            : 'bg-transparent py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <img
                src="/logo.png"
                alt="BestCars"
                width="160"
                height="48"
                decoding="async"
                className="h-12 w-auto object-contain"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                link.hasDropdown ? (
                  <div key={link.path} ref={servicesRef} className="relative">
                    <button
                      onClick={() => setServicesOpen(!servicesOpen)}
                      className={`relative px-5 py-2.5 text-sm font-medium tracking-wide transition-all duration-300 rounded-full flex items-center gap-1 ${
                        isActive(link.path)
                          ? 'text-white'
                          : 'text-white/60 hover:text-white'
                      }`}
                    >
                      {isActive(link.path) && (
                        <motion.div
                          layoutId="navIndicator"
                          className="absolute inset-0 bg-white/10 rounded-full border border-white/10"
                          transition={{ type: 'spring', duration: 0.5 }}
                        />
                      )}
                      <span className="relative z-10">{link.label}</span>
                      <ChevronDown size={14} className={`relative z-10 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {servicesOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 mt-2 w-64 bg-[#111111]/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl shadow-black/40 overflow-hidden"
                        >
                          {serviceLinks.map((sLink) => (
                            <Link
                              key={sLink.path}
                              to={sLink.path}
                              onClick={(e) => handleNav(e, sLink.path)}
                              className="flex items-center gap-3 px-5 py-3.5 text-sm text-white/60 hover:text-white hover:bg-white/5 transition-all"
                            >
                              <sLink.icon size={16} className="text-[#C41E3A]" />
                              {sLink.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={(e) => handleNav(e, link.path)}
                    className={`relative px-5 py-2.5 text-sm font-medium tracking-wide transition-all duration-300 rounded-full ${
                      isActive(link.path)
                        ? 'text-white'
                        : 'text-white/60 hover:text-white'
                    }`}
                  >
                    {isActive(link.path) && (
                      <motion.div
                        layoutId="navIndicator"
                        className="absolute inset-0 bg-white/10 rounded-full border border-white/10"
                        transition={{ type: 'spring', duration: 0.5 }}
                      />
                    )}
                    <span className="relative z-10">{link.label}</span>
                  </Link>
                )
              ))}
            </div>

            {/* CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <div className="flex items-center gap-1.5 mr-1">
                <a
                  href={companyInfo.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full text-white/40 hover:text-white hover:bg-white/5 transition-all duration-300"
                  data-testid="header-facebook"
                  aria-label="Facebook"
                >
                  <Facebook size={16} />
                </a>
                <a
                  href={companyInfo.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full text-white/40 hover:text-white hover:bg-white/5 transition-all duration-300"
                  data-testid="header-instagram"
                  aria-label="Instagram"
                >
                  <Instagram size={16} />
                </a>
              </div>
              <a
                href={companyInfo.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('whatsapp_click', { location: 'navbar_desktop' })}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366]/10 border border-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/20 transition-all duration-300 text-sm font-medium"
              >
                <MessageCircle size={16} />
                WhatsApp
              </a>
              <div
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#C41E3A]/10 border border-[#C41E3A]/20 text-white text-sm font-semibold select-text"
              >
                <Phone size={16} className="text-[#C41E3A]" />
                {companyInfo.phoneDisplay}
              </div>
            </div>

            {/* Mobile Toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 text-white/80 hover:text-white transition-colors"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-[#0a0a0a]/98 backdrop-blur-xl pt-24 px-6"
          >
            <div className="flex flex-col gap-2">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  {link.hasDropdown ? (
                    <div>
                      <button
                        onClick={() => setServicesOpen(!servicesOpen)}
                        className={`w-full flex items-center justify-between px-5 py-4 rounded-2xl text-lg font-medium transition-all ${
                          isActive(link.path)
                            ? 'bg-white/10 text-white'
                            : 'text-white/60 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        {link.label}
                        <ChevronDown size={18} className={`opacity-40 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                      </button>
                      <div className={`ml-4 mt-1 space-y-1 overflow-hidden transition-all duration-200 ${servicesOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                          {serviceLinks.map((sLink) => (
                            <Link
                              key={sLink.path}
                              to={sLink.path}
                              data-testid={`mobile-service-${sLink.path.split('/').pop()}`}
                              onClick={() => setMobileOpen(false)}
                              className="flex items-center gap-3 px-5 py-3 rounded-xl text-sm text-white/50 hover:text-white hover:bg-white/5 transition-all"
                            >
                              <sLink.icon size={14} className="text-[#C41E3A]" />
                              {sLink.label}
                            </Link>
                          ))}
                        </div>
                    </div>
                  ) : (
                    <Link
                      to={link.path}
                      onClick={() => { setMobileOpen(false); setServicesOpen(false); }}
                      className={`flex items-center justify-between px-5 py-4 rounded-2xl text-lg font-medium transition-all ${
                        isActive(link.path)
                          ? 'bg-white/10 text-white'
                          : 'text-white/60 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      {link.label}
                      <ChevronRight size={18} className="opacity-40" />
                    </Link>
                  )}
                </motion.div>
              ))}
            </div>
            <div className="mt-8 flex flex-col gap-3">
              <a
                href={companyInfo.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('whatsapp_click', { location: 'navbar_mobile' })}
                className="flex items-center justify-center gap-2 px-5 py-4 rounded-2xl bg-[#25D366]/10 border border-[#25D366]/20 text-[#25D366] text-base font-medium"
              >
                <MessageCircle size={20} />
                WhatsApp
              </a>
              <a
                href={`tel:${companyInfo.phone}`}
                onClick={() => trackEvent('phone_click', { location: 'navbar_mobile' })}
                className="flex items-center justify-center gap-2 px-5 py-4 rounded-2xl bg-[#C41E3A] text-white text-base font-semibold"
              >
                <Phone size={20} />
                {companyInfo.phoneDisplay}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
